import { useEffect, useState, useRef, RefObject } from 'react';
import { AnnotationOnImage } from '../models/annotation.types';
import {
  calculateHorizontalScrollForMovingAnnotation,
  calculateVerticalScrollForMovingAnnotation,
  getAnnotationIdFromImg,
} from '../components/Solution/annotationsOnPageManagerService';
import { fabric } from 'fabric';
import {
  ANNOTATION_STEP,
  EDGE_PADDING,
  SCROLL_HORIZONTAL_STEP,
  SCROLL_VERTICAL_STEP,
} from '../components/Solution/annotationsConsts';

let keyboardHandlerSelectedAnnotations: ((event: KeyboardEvent) => void) | null = null;
let keyboardHandlerEscAnnotations: ((event: KeyboardEvent) => void) | null = null;

const removeSelectedAnnotationsHandler = () => {
  if (keyboardHandlerSelectedAnnotations) {
    document.removeEventListener('keydown', keyboardHandlerSelectedAnnotations);
    keyboardHandlerSelectedAnnotations = null;
  }
};

const removeEscAnnotationsHandler = () => {
  if (keyboardHandlerEscAnnotations) {
    document.removeEventListener('keydown', keyboardHandlerEscAnnotations);
    keyboardHandlerEscAnnotations = null;
  }
};

export interface UseAnnotationsKeyboardProps {
  canvas: fabric.Canvas | null;
  canvasContainerRef: RefObject<HTMLDivElement | null>;
  lastAddedId: string | null;
  onAnnotationPositionUpdate?: (annotationOnImage: AnnotationOnImage | null, image: fabric.Object) => void;
  updateDelay?: number;
  setIsAnnotationSelected?: (isSelected: boolean) => void;
  onEscKey?: () => void;
  onDeleteKey?: (annotationId: string) => void;
  onPageDownOrUpKey?: (img: fabric.Object, direction: 'up' | 'down') => void;
}

export const useAnnotationsKeyboard = (props: UseAnnotationsKeyboardProps) => {
  const [isAnnotationSelected, setIsAnnotationSelected] = useState<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimeoutRef = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearTimeoutRef();
    };
  }, []);

  const renderAndNotify = (img: fabric.Object) => {
    img.setCoords();
    props.canvas!.renderAll();

    if (props.updateDelay) {
      clearTimeoutRef();
      timeoutRef.current = setTimeout(() => {
        props.onAnnotationPositionUpdate?.(null, img);
        timeoutRef.current = null;
      }, props.updateDelay);
      return;
    }

    props.onAnnotationPositionUpdate?.(null, img);
  };

  const adjustCanvasScroll = (img: fabric.Object, direction: 'up' | 'down' | 'left' | 'right') => {
    if (!props.canvas || !props.canvasContainerRef.current) return;

    const scrollLeft = props.canvasContainerRef.current.scrollLeft;
    const scrollTop = props.canvasContainerRef.current.scrollTop;

    const oCoords = img.oCoords;
    if (!oCoords) return;

    const imageLeftFixed = direction === 'left' ? oCoords.ml.x : oCoords.mr.x;
    const imageLeft = oCoords.ml.x;
    const imageRight = oCoords.mr.x;
    const imageTopFixed = direction === 'up' ? oCoords.mt.y : oCoords.mb.y;
    const imageTop = oCoords.mt.y;
    const imageBottom = oCoords.mb.y;

    const newScrollLeft = calculateHorizontalScrollForMovingAnnotation(
      imageLeftFixed,
      scrollLeft,
      EDGE_PADDING,
      SCROLL_HORIZONTAL_STEP,
      (imageRight - imageLeft) / 2,
      props.canvasContainerRef.current.offsetWidth,
    );

    const newScrollTop = calculateVerticalScrollForMovingAnnotation(
      imageTopFixed,
      scrollTop,
      EDGE_PADDING,
      SCROLL_VERTICAL_STEP,
      (imageBottom - imageTop) / 2,
      props.canvasContainerRef.current.offsetHeight,
    );

    if (scrollTop === newScrollTop && scrollLeft === newScrollLeft) return;

    props.canvasContainerRef.current.scrollTo({
      top: newScrollTop ? newScrollTop : scrollTop,
      left: newScrollLeft ? newScrollLeft : scrollLeft,
    });
  };

  const addKeyboardHandlerSelectedAnnotations = (img: fabric.Object) => {
    keyboardHandlerSelectedAnnotations = (event: KeyboardEvent) => {
      if (!img || !img.left || !img.top || !props.canvas) return;
      switch (event.key) {
        case 'ArrowLeft': {
          event.preventDefault();
          const newLeft = img.left - ANNOTATION_STEP;
          if (newLeft <= 0) return;

          img.left = newLeft;
          adjustCanvasScroll(img, 'left');
          renderAndNotify(img);
          break;
        }
        case 'ArrowRight': {
          event.preventDefault();
          const newLeft = img.left + ANNOTATION_STEP;
          if (newLeft >= (props.canvas.clipPath?.width ?? 0)) return;

          img.left = newLeft;
          adjustCanvasScroll(img, 'right');
          renderAndNotify(img);
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          const newTop = img.top - ANNOTATION_STEP;
          if (newTop <= 0) return;

          img.top = newTop;
          adjustCanvasScroll(img, 'up');
          renderAndNotify(img);
          break;
        }
        case 'ArrowDown': {
          event.preventDefault();
          const newTop = img.top + ANNOTATION_STEP;
          const newTopOnCanvas = newTop * props.canvas.getZoom();
          if (newTopOnCanvas >= props.canvas.getHeight()) return;

          img.top = newTop;
          adjustCanvasScroll(img, 'down');
          renderAndNotify(img);
          break;
        }
        case 'Delete': {
          event.preventDefault();

          props.canvas.discardActiveObject();
          setIsAnnotationSelected(false);
          props.setIsAnnotationSelected?.(false);
          props.onDeleteKey?.(getAnnotationIdFromImg(img) ?? '');
          break;
        }
        case 'PageUp': {
          event.preventDefault();
          props.onPageDownOrUpKey?.(img, 'up');
          break;
        }
        case 'PageDown': {
          event.preventDefault();
          props.onPageDownOrUpKey?.(img, 'down');
          break;
        }
      }
    };

    document.addEventListener('keydown', keyboardHandlerSelectedAnnotations);
  };

  const addKeyboardHandlerEscAnnotations = () => {
    keyboardHandlerEscAnnotations = (event: KeyboardEvent) => {
      if (event.key == 'Escape' && props.canvas) {
        props.canvas.discardActiveObject();
        setIsAnnotationSelected(false);
        props.setIsAnnotationSelected?.(false);
        props.canvas.renderAll();
        props.onEscKey?.();
      }
    };

    document.addEventListener('keydown', keyboardHandlerEscAnnotations);
  };

  useEffect(() => {
    if (!props.canvas) return;

    if (isAnnotationSelected && !keyboardHandlerSelectedAnnotations) {
      const active = props.canvas.getActiveObject();
      if (active) {
        addKeyboardHandlerSelectedAnnotations(active);
      }
    }

    props.canvas.on('selection:created', (obj) => {
      setIsAnnotationSelected(true);
      props.setIsAnnotationSelected?.(true);
      if (obj.selected && obj.selected.length > 0) {
        removeSelectedAnnotationsHandler();
        addKeyboardHandlerSelectedAnnotations(obj.selected![0]);
      }
    });
    props.canvas.on('selection:updated', (obj) => {
      removeSelectedAnnotationsHandler();
      if (obj.selected && obj.selected.length > 0) {
        addKeyboardHandlerSelectedAnnotations(obj.selected[0]);
      }
    });
    props.canvas.on('selection:cleared', (obj) => {
      setIsAnnotationSelected(false);
      props.setIsAnnotationSelected?.(false);
      removeSelectedAnnotationsHandler();
    });

    addKeyboardHandlerEscAnnotations();

    return () => {
      props.canvas?.off('selection:created');
      props.canvas?.off('selection:updated');
      props.canvas?.off('selection:cleared');
      removeSelectedAnnotationsHandler();
      removeEscAnnotationsHandler();
    };
  }, [props.canvas, isAnnotationSelected, props.onDeleteKey, props.onPageDownOrUpKey, props.lastAddedId]);

  return isAnnotationSelected;
};

export default useAnnotationsKeyboard;
